import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { FocusTrap } from '../utils';
import { TOGGLE_RIGHT_DRAWER_EVENT } from '../ThemeBase';

/**
  * @typedef {Object} SearchGroup
  * @param {string} name
  * @param {any[]} results
  */

@Component({
  template: '#wetheme-right-drawer-template',
})
class RightDrawer extends Vue {
  /** @type {boolean} */
  isOpen = false;

  /** @type {'search' | 'page'} */
  type = 'search';

  /** @type {number | null} */
  lineQuantityUpdating = null;

  /** @type {boolean} */
  searching = false;

  /** @type {string} */
  searchQuery = ''

  /** @type {SearchGroup[]} */
  searchGroups = [];

  /** @type {number | null} */
  searchChangeTimer = null;

  /** @type {string} */
  searchSummary = '';

  /** @type {boolean} */
  searchHasResults = false

  /** @type {number} */
  searchResultsNum = 0;

  /** @type {string} */
  @Prop({required: true})
  searchUrl;

  /** @type {string} */
  @Prop({required: false})
  isPredictive;

  // /** @type {string} */
  @Prop({required: false})
  languageUrl;

  /** @type {HTMLElement | null} */
  triggerElement = null;

  data() {
    return {
      isOpen: false,
      type: 'search',
      lineQuantityUpdating: null,
      searchQuery: '',
      searchResults: []
    }
  }

  @Watch('searchQuery')
  onSearchChange() {
    if(this.isSearchPredictive) {
      if(this.searchQuery > '') {
        if (this.searchChangeTimer) {
          window.clearTimeout(this.searchChangeTimer);
        }
        this.searchChangeTimer = window.setTimeout(() => {
          this.performSearch();
          this.searchChangeTimer = null;
        }, 250);
      }
      else {
        this.searchResultsNum = 0;
        this.searchHasResults = false;
        this.searchGroups = [];
        this.searching = false;
        this.searchSummary = '';
      }
    }
  }

  /** @returns {import('../ThemeBase').default} */
  get theme() {
    // @ts-ignore
    return this.$root.theme;
  }

  mounted() {
    document.documentElement.addEventListener(TOGGLE_RIGHT_DRAWER_EVENT, this.handleToggle);
    // A custom event to toggle the right drawer from within web components
    window.eventBus.on('toggle:right:drawer', this.handleToggle);
    window.eventBus.on('close:right:drawer', this.close);
  }

  beforeDestroy() {
    document.documentElement.removeEventListener(TOGGLE_RIGHT_DRAWER_EVENT, this.handleToggle);
    window.eventBus.off('toggle:right:drawer', this.handleToggle);
    window.eventBus.on('close:right:drawer', this.close);
  }

  handleToggle(event) {
    const { type, forceOpen, params } = event.detail ? event.detail : event;
    if (params && params.url && type === 'page') {
      this.getGenericPage(params.url);
    }

    this.toggle(type, forceOpen);
  }

  close() {
    document.body.classList.remove('js-drawer-open');
    document.body.classList.remove('js-drawer-open-right');
    this.isOpen = false;
    if (this.focusTrap) {
      this.focusTrap.destroy();
    }
    // Restore focus to the triggering element
    if (this.triggerElement) {
      this.triggerElement.focus();
      this.triggerElement = null; // Clear the reference after focusing
    }
  }

  open() {
    // Store the element that triggered the drawer to open
    this.triggerElement = document.activeElement;

    document.body.classList.add('js-drawer-open');
    document.body.classList.add('js-drawer-open-right');
    this.isOpen = true;
    if (this.type === 'search') {
      this.$nextTick(() => {
        this.focusTrap = new FocusTrap(this.$el, this.$refs.searchText);
      });
    } else {
      this.focusTrap = new FocusTrap(this.$el);
    }
  }

  toggle(type, forceOpen = undefined) {
    if (forceOpen === true) {
      this.type = type;
      this.open();
    } else if (forceOpen === false) {
      this.close();
    } else if (this.isOpen) {
      this.close();
    } else {
      this.type = type;
      this.open();
    }
  }

  /**
   * Fetch page
   *
   * @param {string} url
   */
  async fetchPage(url) {
    const response = await window.fetch(url);
    const parser = new DOMParser();
    const doc = parser.parseFromString(await response.text(), 'text/html');
    const pageContent = doc.querySelector('.page-main--wrapper');
    return pageContent;
  }

  /**
   * Page url
   *
   * @param {string} url
   */
  async getGenericPage(url) {
    const pageContent = await this.fetchPage(url);
    const target = /** @type {HTMLElement} */ (this.$refs.pageContent);
    target.appendChild(pageContent);
  }

  onSearchSubmit() {
    window.location.href = this.searchPageUrlWithQuery;
    return;
  }

  clearSearchInput() {
    this.searchQuery = '';
    (/** @type {HTMLElement} */ (this.$refs.searchText)).focus();
  }

  get isSearchPredictive() {
    return (this.isPredictive == 'true');
  }

  get searchUrlWithQuery() {
    const languageParam = !this.languageUrl || this.languageUrl == '/' ? '' : this.languageUrl;
    if(this.isSearchPredictive) {
      return`${languageParam}${this.searchUrl}?q=${encodeURIComponent(this.searchQuery)}&resources[limit]=3&resources[limit_scope]=each`;
    }
    else {
      return`${languageParam}/search?q=${encodeURIComponent(this.searchQuery)}`;
    }
  }

  get searchPageUrlWithQuery() {
    const languageParam = !this.languageUrl || this.languageUrl == '/' ? '' : this.languageUrl;
    return`${languageParam}/search?q=${encodeURIComponent(this.searchQuery)}*`;
  }

  async performSearch() {
    this.searching = true;
    this.searchHasResults = false;
    this.searchResultsNum = 0;
    this.searchSummary = '';
    let data;
    try {
      const response = await fetch(`${this.searchUrlWithQuery}`);

      if (!response.ok) {
        // FIXME handle error
      }
      data = await response.json();

    } catch (e) {
      console.error("Search failed", e);
      // We'll redirect the user to /search page when search fails (for whatever reason)
      window.location.href = this.searchPageUrlWithQuery;
    }

    const resultGroups = new Map();

    // Loop over groups e.g. Products / Pages / Articles / Collections
    Object.keys(data.resources.results).forEach((resultType) => {
      let group = resultGroups.get(resultType);
      let result = data.resources.results[resultType];

      if (!group) {
        group = {
          name: {
            'queries': this.theme.translations.suggestions,
            'articles': this.theme.translations.articles,
            'pages': this.theme.translations.pages,
            'products': this.theme.translations.products,
            'collections': this.theme.translations.collections,
          }[resultType],
          results: [],
        };
        resultGroups.set(resultType, group);
      }

      // Format price.
      let resultWithPrice = result.map(item => {
        var temp = Object.assign({}, item);
        const pageContainer = document.getElementById('PageContainer');
        let showCurrencyCode = pageContainer.dataset.showCurrencyCode;
        let currencyCode = pageContainer.dataset.currencyCode;

        if(temp.price) {
          if (currencyCode && showCurrencyCode == 'true') {
            temp.price = `${this.theme.formatMoney(temp.price)} ${currencyCode}`;
          } else {
            temp.price = this.theme.formatMoney(temp.price);
          }
        }
        return temp;
      });

      group.results = resultWithPrice;

      if(group.results && group.results.length > 0) {
        this.searchResultsNum += result.length;
        this.searchHasResults = true;
      }
    });

    if (!this.searchHasResults && this.searchQuery > '') {
      this.searchSummary = this.theme.translations.no_results_html.replace('{{ terms }}', this.searchQuery);
    }
    this.searching = false;
    this.searchGroups = Array.from(resultGroups.values());
  }
}

export default RightDrawer;
